/**
 * =============================================================================
 * REPLY ITEM COMPONENT
 * =============================================================================
 *
 * Single reply item with moderation actions, solution toggle, and the inline
 * reject expansion (ADR-025 A-Q2 — replaces the nested RejectionReasonModal).
 *
 * GAP-008: Admin Reply & Solution Management UI
 *
 * =============================================================================
 */

import React, { useEffect, useRef, useState } from 'react';
import { Badge, Box, Button, Flex, FlexItem, Text, Textarea, Tooltip } from '@bigcommerce/big-design';
import { CheckIcon, CloseIcon, VisibilityOffIcon } from '@bigcommerce/big-design-icons';
import { ReplyStatusBadge } from './ReplyStatusBadge';
import { SolutionToggle } from './SolutionToggle';
import { ReplyImageStrip } from './ReplyImageStrip';
import { formatDate } from '../../utils/formatters';
import type { AdminReplyListItem } from '@find-my-part/types';
import { tokens } from '../../tokens';

/** Maximum rejection-reason length (unchanged from the replaced modal — DR-10). */
const MAX_REASON_LENGTH = 1000;

/**
 * Props for ReplyItem component.
 */
export interface ReplyItemProps {
  /** Reply data — uses AdminReplyListItem from @find-my-part/types */
  reply: AdminReplyListItem;
  /**
   * Auth token for the per-reply image strip's authenticated blob fetches
   * (ADR-022 §14 D5). Threaded from ReplyModerationPanel.
   */
  token: string;
  /** Callback when approve button is clicked */
  onApprove: (replyId: string) => Promise<void>;
  /** Callback when reject button is clicked (expands the inline reject form) */
  onReject: (replyId: string) => void;
  /** Callback when hide button is clicked */
  onHide: (replyId: string) => Promise<void>;
  /** Callback when solution toggle is clicked */
  onToggleSolution: (replyId: string, isSolution: boolean) => Promise<void>;
  /** Whether this reply has a pending action */
  isPending: boolean;
  /** Whether this row owns the active solution loading indicator */
  isSolutionActionPending?: boolean;
  /** Panel-wide solution guard; disables every solution control */
  solutionActionsDisabled?: boolean;
  /**
   * B-4 row choreography: freeze approve/hide/solution actions while any
   * inline reject form is live. Reject remains available on sibling rows so
   * the existing guarded row-switch flow can prompt before discarding.
   */
  contextActionsDisabled?: boolean;
  /** Whether this row's inline reject form is expanded (ADR-025 A-Q2 / M4) */
  isRejectFormOpen: boolean;
  /** True while this row's rejection request is in flight */
  isRejectSubmitting: boolean;
  /** Submit the inline rejection with the trimmed reason */
  onConfirmReject: (replyId: string, reason: string) => Promise<void>;
  /** Collapse the inline reject form without rejecting */
  onCancelReject: () => void;
  /**
   * Internal review R1 / external round 2: true (on the OPEN row only) while
   * a draft-destroying intent — a Reject click on a DIFFERENT row, a page
   * change, or a parent-dialog close — waits on this form's draft decision:
   * no draft applies it immediately; a live draft raises the discard prompt.
   */
  isRejectIntentRequested?: boolean;
  /** Resolve the parked intent: proceed=true applies it (R1 / ext-W2). */
  onResolveRejectIntent?: (proceed: boolean) => void;
  /**
   * ext-W2-C2: reports whether this row's open form carries a non-empty
   * (trimmed) draft — the parent dialog's dirty-close guard consumes it.
   */
  onRejectDraftChange?: (dirty: boolean) => void;
  /** Optional test ID prefix */
  testId?: string;
}

/**
 * Maps user type to badge configuration.
 */
const USER_TYPE_CONFIG: Record<string, { label: string; variant: 'primary' | 'secondary' | 'success' }> = {
  ADMIN: { label: 'Admin', variant: 'primary' },
  CUSTOMER: { label: 'Customer', variant: 'success' },
  GUEST: { label: 'Guest', variant: 'secondary' },
};

/**
 * Props for the inline reject form.
 */
interface InlineRejectFormProps {
  /** Reply being rejected */
  replyId: string;
  /** Author name — labels the expansion for screen readers */
  authorName: string;
  /** True while the rejection request is in flight */
  isSubmitting: boolean;
  /** Confirm rejection with the trimmed reason */
  onConfirm: (replyId: string, reason: string) => Promise<void>;
  /** Collapse the form without rejecting */
  onCancel: () => void;
  /** R1/ext-W2: a draft-destroying intent is waiting on this form's verdict */
  intentRequested?: boolean;
  /** R1/ext-W2: deliver the verdict — proceed=true applies the parked intent */
  onIntentResolve?: (proceed: boolean) => void;
  /** ext-W2-C2: report non-empty-draft presence upward (dirty-close guard) */
  onDraftChange?: (dirty: boolean) => void;
  /** Optional test ID prefix */
  testId?: string;
}

/**
 * Inline rejection-reason form (ADR-025 A-Q2 / blueprint M4 — replaces the
 * nested `RejectionReasonModal`, removing the modal-in-modal).
 *
 * Reason-capture semantics are UNCHANGED from the replaced modal (DR-10):
 * required trimmed non-empty reason, ≤1000 characters with a live counter,
 * inputs disabled + submit loading while in flight, autofocus on expand.
 *
 * Esc semantics (DR-4): Escape collapses ONLY this form — `stopPropagation`
 * keeps it from the parent Question-Details dialog's own Escape handler.
 * React's synthetic `stopPropagation` also stops the NATIVE event at the
 * modal's portal container, so big-design's document-level focus-trap
 * `escapeDeactivates` listener (bubble phase) never sees it either — the
 * parent dialog stays open WITH its trap intact.
 *
 * A non-empty (trimmed) draft is never discarded silently by ANY guarded
 * route: Esc, Cancel, a Reject click on another row (R1), a page change
 * (ext-W2-F1), and the parent dialog's own close routes via the dirty-close
 * nonce (ext-W2-C2) all swap the actions for the inline discard prompt
 * first; only an explicit "Discard" activation loses the draft (Esc at the
 * prompt backs out to editing). Remaining unguarded: the queue-level close
 * after a CONFIRMED question-reject (deliberate completion) and a prop-level
 * question/token switch — BL-ADMIN-REPLY-DRAFT-PARENT-CLOSE-001 (B-4).
 * Draft state lives here, so collapse always resets the form.
 */
function InlineRejectForm({
  replyId,
  authorName,
  isSubmitting,
  onConfirm,
  onCancel,
  intentRequested = false,
  onIntentResolve,
  onDraftChange,
  testId,
}: InlineRejectFormProps): React.ReactElement {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isConfirmingDiscard, setIsConfirmingDiscard] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const keepEditingRef = useRef<HTMLButtonElement>(null);

  // Autofocus the reason field when the row expands (DR-10 parity with the
  // replaced modal). The form mounts only while expanded, so mount ≡ expand.
  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  // Focus follows the discard prompt: to "Keep editing" (the safe default)
  // when it appears, back to the textarea when it is dismissed.
  const prevConfirmingDiscard = useRef(false);
  useEffect(() => {
    if (isConfirmingDiscard && !prevConfirmingDiscard.current) {
      keepEditingRef.current?.focus();
    } else if (!isConfirmingDiscard && prevConfirmingDiscard.current) {
      textareaRef.current?.focus();
    }
    prevConfirmingDiscard.current = isConfirmingDiscard;
  }, [isConfirmingDiscard]);

  const trimmedReason = reason.trim();
  const isValid = trimmedReason.length > 0 && trimmedReason.length <= MAX_REASON_LENGTH;
  const hasDraft = trimmedReason.length > 0;

  // R1 (internal review), generalized ext-W2: ANY draft-destroying intent —
  // a Reject click on another row, a page change, a parent-dialog close —
  // routes through this form, never a silent discard. No draft → apply it
  // immediately; a live draft raises the same discard prompt, where "Keep
  // editing" (or Esc) denies it and only an explicit Discard applies it. The
  // draft state is read via a ref so the effect fires exactly on the
  // request edge.
  const hasDraftRef = useRef(hasDraft);
  hasDraftRef.current = hasDraft;
  useEffect(() => {
    if (!intentRequested) return;
    if (isSubmitting) {
      onIntentResolve?.(false); // locked mid-flight — the panel also guards this
      return;
    }
    if (!hasDraftRef.current) {
      onIntentResolve?.(true);
      return;
    }
    setIsConfirmingDiscard(true);
  }, [intentRequested, isSubmitting, onIntentResolve]);

  // ext-W2-C2: keep the parent dialog's dirty-close guard current. Reported
  // on every draft-presence change; cleared on unmount (collapse).
  useEffect(() => {
    onDraftChange?.(hasDraft);
  }, [hasDraft, onDraftChange]);
  useEffect(() => () => onDraftChange?.(false), [onDraftChange]);

  const requestClose = (): void => {
    if (isSubmitting) return;
    if (hasDraft) {
      setIsConfirmingDiscard(true);
      return;
    }
    onCancel();
  };

  /** Return to editing from the prompt; a parked intent is denied. */
  const keepEditing = (): void => {
    setIsConfirmingDiscard(false);
    if (intentRequested) onIntentResolve?.(false);
  };

  /** Explicit Discard: apply the parked intent, or plain-close the form. */
  const discardDraft = (): void => {
    if (intentRequested) {
      onIntentResolve?.(true);
      return;
    }
    onCancel();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== 'Escape') return;
    // DR-4 / M4: Esc collapses ONLY this inline form — stopped here so it can
    // never reach (and close) the parent Question-Details dialog.
    event.stopPropagation();
    if (isSubmitting) return; // Esc is inert mid-submit (A-Q1 F6 parity)
    if (isConfirmingDiscard) {
      // Esc backs out one level: return to editing. Only the explicit
      // "Discard" control ever destroys a draft.
      keepEditing();
      return;
    }
    requestClose();
  };

  const handleSubmit = async (): Promise<void> => {
    if (!isValid || isSubmitting) return;
    await onConfirm(replyId, trimmedReason);
  };

  const handleReasonChange = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
    const value = event.target.value;
    setReason(value);

    if (error) {
      setError(null);
    }
    if (value.trim().length > MAX_REASON_LENGTH) {
      setError('Reason must be 1,000 characters or less');
    }
  };

  return (
    <Box
      role="group"
      aria-label={`Reject reply by ${authorName}`}
      marginTop="small"
      padding="small"
      style={{ border: `1px solid ${tokens.color.border.default}`, borderRadius: tokens.dimension.radius.lg }}
      onKeyDown={handleKeyDown}
      data-testid={testId}
    >
      <Box marginBottom="small">
        <Text>
          Please provide a reason for rejecting this reply.
          This is recorded in the audit trail.
        </Text>
      </Box>

      <Box marginBottom="small">
        <Textarea
          ref={textareaRef}
          label="Reason"
          required
          value={reason}
          onChange={handleReasonChange}
          rows={3}
          maxLength={MAX_REASON_LENGTH}
          error={error ?? undefined}
          disabled={isSubmitting}
          data-testid={testId ? `${testId}-textarea` : undefined}
          aria-label="Rejection reason"
        />
      </Box>

      <Flex justifyContent="flex-end" marginBottom="small">
        <FlexItem>
          <Text color="secondary">
            {reason.length} / {MAX_REASON_LENGTH.toLocaleString()} chars
          </Text>
        </FlexItem>
      </Flex>

      {isConfirmingDiscard ? (
        <Flex
          flexWrap="wrap"
          flexGap="0.5rem"
          alignItems="center"
          justifyContent="flex-end"
          data-testid={testId ? `${testId}-discard-confirm` : undefined}
        >
          <FlexItem>
            <Text>Discard your rejection reason?</Text>
          </FlexItem>
          <FlexItem>
            <Button
              ref={keepEditingRef}
              variant="secondary"
              onClick={keepEditing}
              data-testid={testId ? `${testId}-keep-editing` : undefined}
            >
              Keep editing
            </Button>
          </FlexItem>
          <FlexItem>
            <Button
              variant="primary"
              actionType="destructive"
              onClick={discardDraft}
              data-testid={testId ? `${testId}-discard` : undefined}
            >
              Discard
            </Button>
          </FlexItem>
        </Flex>
      ) : (
        <Flex flexWrap="wrap" flexGap="0.5rem" justifyContent="flex-end">
          <FlexItem>
            <Button
              variant="secondary"
              onClick={requestClose}
              disabled={isSubmitting}
              data-testid={testId ? `${testId}-cancel` : undefined}
            >
              Cancel
            </Button>
          </FlexItem>
          <FlexItem>
            <Button
              variant="primary"
              actionType="destructive"
              onClick={() => void handleSubmit()}
              disabled={!isValid || isSubmitting}
              isLoading={isSubmitting}
              data-testid={testId ? `${testId}-submit` : undefined}
            >
              Reject Reply
            </Button>
          </FlexItem>
        </Flex>
      )}
    </Box>
  );
}

/**
 * Single reply item with moderation actions, solution toggle, and the inline
 * reject expansion (rendered when `isRejectFormOpen`).
 *
 * Action visibility rules:
 * - PENDING_REVIEW: [Approve] [Reject]
 * - APPROVED: [Hide] + SolutionToggle
 * - REJECTED: (no actions — terminal state)
 * - HIDDEN: (no actions — terminal state)
 *
 * @example
 * ```tsx
 * <ReplyItem
 *   reply={replyData}
 *   onApprove={handleApprove}
 *   onReject={handleReject}
 *   onHide={handleHide}
 *   onToggleSolution={handleToggleSolution}
 *   isPending={false}
 *   isRejectFormOpen={false}
 *   isRejectSubmitting={false}
 *   onConfirmReject={handleConfirmReject}
 *   onCancelReject={handleCancelReject}
 * />
 * ```
 */
export function ReplyItem({
  reply,
  token,
  onApprove,
  onReject,
  onHide,
  onToggleSolution,
  isPending,
  isSolutionActionPending = false,
  solutionActionsDisabled = false,
  contextActionsDisabled = false,
  isRejectFormOpen,
  isRejectSubmitting,
  onConfirmReject,
  onCancelReject,
  isRejectIntentRequested = false,
  onResolveRejectIntent,
  onRejectDraftChange,
  testId,
}: ReplyItemProps): React.ReactElement {
  const userTypeConfig = USER_TYPE_CONFIG[reply.userType] ?? USER_TYPE_CONFIG.GUEST;
  const isPendingReview = reply.moderationStatus === 'PENDING_REVIEW';
  const isApproved = reply.moderationStatus === 'APPROVED';
  const hasActions = isPendingReview || isApproved;
  const contextualActionPending = isPending || contextActionsDisabled;

  // Focus restoration when the inline reject form collapses (M4/M11 posture:
  // never drop focus to <body>). The row's Reject control is RE-QUERIED from
  // the CURRENT DOM at restore time — never a node captured at expand time,
  // because a re-render may have re-created the row's DOM. After a SUCCESSFUL
  // reject the row is terminal and the control is gone, so focus lands on the
  // row container itself (tabIndex={-1}); after Esc/Cancel or a rolled-back
  // failure the re-queried control exists and receives focus.
  const rowRef = useRef<HTMLDivElement>(null);
  const wasRejectFormOpen = useRef(false);
  useEffect(() => {
    if (wasRejectFormOpen.current && !isRejectFormOpen) {
      // R1: restore only when the collapse actually ORPHANED focus (an
      // unmount sends it to <body>) or focus is still within this row. On a
      // row-switch the newly opened form's autofocus may already hold focus
      // — never steal it back to this row's trigger.
      const active = document.activeElement;
      const orphaned =
        !active || active === document.body || rowRef.current?.contains(active) === true;
      if (orphaned) {
        const row = rowRef.current;
        const rejectTrigger = row?.querySelector<HTMLElement>('[data-inline-reject-trigger]');
        rejectTrigger?.focus();
        // Took-check (same class as the A-0 CI root-cause): a momentarily
        // disabled trigger silently refuses focus — the row container
        // (tabIndex={-1}) is always focusable, so focus never strands on
        // <body>.
        if (document.activeElement !== rejectTrigger) {
          row?.focus();
        }
      }
    }
    wasRejectFormOpen.current = isRejectFormOpen;
  }, [isRejectFormOpen]);

  return (
    <Box
      ref={rowRef}
      role="listitem"
      tabIndex={-1}
      padding="medium"
      marginBottom="small"
      style={{
        // B-6 §6.3 de-encoding (M19, final-decisions Q2): the ReplyStatusBadge is
        // the SINGLE moderation-status channel — the status border-left and the
        // terminal background tint are removed. The solution tint stays (it
        // encodes the ADR-017 solution axis, not moderation status), as does the
        // transient pending-action opacity.
        border: `1px solid ${tokens.color.border.default}`,
        borderRadius: tokens.dimension.radius.lg,
        backgroundColor: reply.isSolution ? tokens.color.status.success.surface : tokens.color.surface.base,
        opacity: isPending ? 0.7 : 1,
        transition: 'opacity 0.15s ease',
      }}
      data-reply-id={reply.replyId}
      data-testid={testId}
    >
      {/* Header Row */}
      <Flex
        flexWrap="wrap"
        flexGap="0.5rem"
        alignItems="center"
        marginBottom="small"
      >
        <FlexItem>
          <Text bold>{reply.authorName}</Text>
        </FlexItem>
        <FlexItem>
          <Badge
            variant={userTypeConfig.variant}
            label={userTypeConfig.label}
            aria-label={`User type: ${userTypeConfig.label}`}
          />
        </FlexItem>
        <FlexItem>
          <ReplyStatusBadge
            status={reply.moderationStatus}
            data-testid={testId ? `${testId}-status` : undefined}
          />
        </FlexItem>
        <FlexItem flexGrow={1} />
        <FlexItem>
          <Text color="secondary">
            {formatDate(reply.createdAt)}
          </Text>
        </FlexItem>
      </Flex>

      {/* Reply Body */}
      <Box marginBottom={hasActions || reply.images.length > 0 ? 'small' : undefined}>
        <Text>{reply.body}</Text>
      </Box>

      {/* Per-reply image context (ADR-022 §14 D5). Renders nothing when the reply
          has no images; shows every moderation state to the moderator. */}
      <ReplyImageStrip
        images={reply.images}
        token={token}
        testId={testId ? `${testId}-images` : undefined}
      />

      {/* Actions Row */}
      {hasActions && (
        <Flex
          flexWrap="wrap"
          flexGap="0.5rem"
          alignItems="center"
          justifyContent="flex-end"
          style={{ marginTop: tokens.dimension.space.sm }}
        >
          {isPendingReview && (
            <>
              <FlexItem>
                <Button
                  variant="primary"
                  onClick={() => onApprove(reply.replyId)}
                  disabled={contextualActionPending}
                  iconLeft={<CheckIcon />}
                  aria-label={`Approve reply by ${reply.authorName}`}
                  data-testid={testId ? `${testId}-approve` : undefined}
                >
                  Approve
                </Button>
              </FlexItem>
              <FlexItem>
                <Button
                  variant="secondary"
                  actionType="destructive"
                  onClick={() => onReject(reply.replyId)}
                  disabled={isPending}
                  iconLeft={<CloseIcon />}
                  aria-label={`Reject reply by ${reply.authorName}`}
                  data-inline-reject-trigger="true"
                  data-testid={testId ? `${testId}-reject` : undefined}
                >
                  Reject
                </Button>
              </FlexItem>
            </>
          )}

          {isApproved && (
            <>
              <FlexItem>
                {reply.isSolution ? (
                  <Tooltip trigger={
                    <Button
                      variant="secondary"
                      disabled
                      iconLeft={<VisibilityOffIcon />}
                      aria-label="Cannot hide — remove solution first"
                      data-testid={testId ? `${testId}-hide` : undefined}
                    >
                      Hide
                    </Button>
                  } placement="top">
                    Remove solution before hiding this reply
                  </Tooltip>
                ) : (
                  <Button
                    variant="secondary"
                    onClick={() => onHide(reply.replyId)}
                    disabled={contextualActionPending}
                    iconLeft={<VisibilityOffIcon />}
                    aria-label={`Hide reply by ${reply.authorName}`}
                    data-testid={testId ? `${testId}-hide` : undefined}
                  >
                    Hide
                  </Button>
                )}
              </FlexItem>
              <FlexItem>
                <SolutionToggle
                  replyId={reply.replyId}
                  isSolution={reply.isSolution}
                  isApproved={true}
                  onToggle={onToggleSolution}
                  isPending={isSolutionActionPending}
                  disabled={
                    contextActionsDisabled ||
                    solutionActionsDisabled ||
                    (isPending && !isSolutionActionPending)
                  }
                  testId={testId ? `${testId}-solution` : undefined}
                />
              </FlexItem>
            </>
          )}
        </Flex>
      )}

      {/* Inline reject expansion (ADR-025 A-Q2 / M4). Deliberately rendered
          OUTSIDE the hasActions block above: the optimistic PENDING_REVIEW →
          REJECTED transition drops hasActions the instant a reject is
          submitted, and the in-flight form must survive that transition to
          show its loading state (DR-4/M4). */}
      {isRejectFormOpen && (
        <InlineRejectForm
          replyId={reply.replyId}
          authorName={reply.authorName}
          isSubmitting={isRejectSubmitting}
          onConfirm={onConfirmReject}
          onCancel={onCancelReject}
          intentRequested={isRejectIntentRequested}
          onIntentResolve={onResolveRejectIntent}
          onDraftChange={onRejectDraftChange}
          testId={testId ? `${testId}-reject-form` : undefined}
        />
      )}
    </Box>
  );
}
